<template>
  <div
    class="col"
    data-cy="handover-rooms-step">
    <div class="text-center text-danger my-2" />
    <div
      class="row">
      <div class="col-md-5">
        <h3 data-cy="handover-rooms-selected-title">
          {{ $t('message.processHandover.rooms.selectedRooms') }}
          <coozzy-info-circle-icon
            v-b-tooltip.hover.html="$t('message.processHandover.rooms.selectedRoomsTooltip')"
            class="ml-2 mb-2 tooltip-message" />
        </h3>
        <coozzy-card
          class="list-group w-100 p-0">
          <draggable
            v-model="selectedRooms"
            @end="updateOrder">
            <div
              v-for="room in selectedRooms"
              :key="room.id"
              class="list-group-item"
              data-cy="handover-rooms-selected-item">
              <coozzy-form-input
                v-if="editingRoom && editingRoom.id === room.id"
                v-model="room.name"
                data-cy="handover-rooms-selected-item-name-input"
                @blur="finishEdit(room)"
                @keyup.enter="finishEdit(room)" />
              <span
                v-else
                data-cy="handover-rooms-selected-item-name">{{ room.name }}</span>
              <coozzy-button
                v-if="!(editingRoom && editingRoom.id === room.id)"
                data-cy="handover-rooms-selected-item-delete-button"
                design="transparent"
                class="p-0 ml-1 mb-2  float-right"
                @click="showDeleteConfirmation(room, $event)">
                <coozzy-delete-icon />
              </coozzy-button>
              <coozzy-button
                v-if="editingRoom && editingRoom.id === room.id"
                data-cy="handover-rooms-selected-item-save-button"
                design="transparent"
                class="p-0 mb-2 ml-2 save-icon"
                @click="finishEdit(room)">
                <coozzy-save-icon />
              </coozzy-button>
              <coozzy-button
                v-else
                data-cy="handover-rooms-selected-item-edit-button"
                design="transparent"
                class="p-0 mb-2 ml-2 float-right"
                @click="editRoom(room)">
                <coozzy-edit-icon />
              </coozzy-button>
            </div>
          </draggable>
        </coozzy-card>
        <coozzy-button
          data-cy="handover-rooms-add-room-button"
          class="col-sm-12 col-lg-4 col-md-4 float-md-right mt-3"
          design="prop-green"
          @click="addRoom">
          {{ $t('message.processHandover.rooms.addRoom') }}
        </coozzy-button>
        <b-modal
          :id="'confirmationModalRoom_' + objectId"
          :ref="'confirmationModalRoom_' + objectId"
          data-cy="handover-rooms-delete-confirm-modal"
          no-close-on-backdrop
          hide-footer
          :title="$t('message.processHandover.confirmationDeletion.title')">
          <template>
            <div class="col p-0">
              <p>{{ $t('message.processHandover.confirmationDeletion.roomBody') }}</p>
            </div>
            <div class="col p-0">
              <coozzy-button
                data-cy="handover-rooms-delete-cancel-button"
                size="small"
                class="mb-0 border"
                design="transparent"
                @click="$bvModal.hide('confirmationModalRoom_' + objectId)">
                {{ $t('message.generic.cancel') }}
              </coozzy-button>
              <coozzy-button
                data-cy="handover-rooms-delete-confirm-button"
                size="small"
                class="float-right mb-0"
                design="green"
                @click="deleteRoom">
                {{ $t('message.generic.delete') }}
              </coozzy-button>
            </div>
          </template>
        </b-modal>
      </div>
      <div class="col-md-5 offset-2">
        <h3 data-cy="handover-rooms-suggestions-title">
          {{ source === 'template' ? $t('message.processHandoverTemplate.defineRooms') : $t('message.processHandover.rooms.availableRooms') }}
          <coozzy-info-circle-icon
            v-if="source !== 'template' && suggestionRooms.length > 0"
            v-b-tooltip.hover.html="$t('message.processHandover.rooms.availableRoomsTooltip')"
            class="ml-2 mb-2 tooltip-message" />
        </h3>
        <p
          v-if="suggestionRooms.length > 0 && templateObject"
          class="m-0"
          data-cy="handover-rooms-template-name">
          {{ $t('message.settings.template.template') }}: {{ templateObject.name }}
        </p>
        <coozzy-card
          class="list-group w-100 p-0">
          <div
            v-for="room in availableRooms"
            :key="room.id"
            class="list-group-item"
            data-cy="handover-rooms-suggestion-item">
            <span data-cy="handover-rooms-suggestion-item-name">{{ room.name }}</span>
            <coozzy-button
              data-cy="handover-rooms-suggestion-item-add-button"
              design="prop-green"
              class="p-0 ml-1 float-right px-2 add-to-available"
              @click="addRoomMethod(room)">
              +
            </coozzy-button>
          </div>
        </coozzy-card>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import { formatting } from '@/mixins/formatting'
import { obj } from '@/mixins/object'
import { onboarding } from '@/mixins/onboarding'
import { routeChecks } from '@/mixins/routeChecks'
import { algolia } from '@/mixins/algolia'
import { mapActions, mapGetters } from 'vuex'
import CoozzyEditIcon from '@/framework/components/icons/CoozzyEditIcon.vue'
import CoozzyButton from '@/framework/components/button/CoozzyButton.vue'
import CoozzyDeleteIcon from '@/framework/components/icons/CoozzyDeleteIcon.vue'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput.vue'
import CoozzySaveIcon from '@/framework/components/icons/CoozzySaveIcon.vue'
import CoozzyInfoCircleIcon from '@/framework/components/icons/CoozzyInfoCircleIcon.vue'
import CoozzyCard from '@/framework/components/card/CoozzyCard.vue'
import draggable from 'vuedraggable'
import { ObjectroomRoom, ObjectobjectObject, ObjecthandoverProtocolTemplate } from '@/types/api'
import { HandoverDevice, HandoverRoom } from '@/types/models'

export default Vue.extend({
  name: 'HandoverRoomsStep',
  components: {
    CoozzyCard,
    CoozzyInfoCircleIcon,
    CoozzySaveIcon,
    CoozzyFormInput,
    CoozzyDeleteIcon,
    CoozzyButton,
    CoozzyEditIcon,
    draggable
  },
  mixins: [formatting, obj, onboarding, routeChecks, algolia],
  props: {
    suggestionRooms: {
      type: Array as PropType<ObjectroomRoom[]>,
      required: false,
      default: () => []
    },
    templateObject: {
      type: Object as PropType<ObjecthandoverProtocolTemplate | null>,
      required: false,
      default: () => null
    },
    object: {
      type: Object as PropType<ObjectobjectObject | null>,
      default: () => null
    },
    selfRooms: {
      type: Array as PropType<HandoverRoom[]>,
      required: false,
      default: () => []
    },
    preSelectedRooms: {
      type: Array as PropType<HandoverRoom[]>,
      required: false,
      default: () => []
    },
    source: {
      type: String,
      required: false,
      default: () => ''
    }
  },
  data() {
    return {
      deletedRooms: [] as (string | number)[],
      rooms: [] as HandoverRoom[],
      availableRooms: [] as HandoverRoom[],
      selectedRooms: [] as HandoverRoom[],
      selectedEntries: [] as HandoverRoom[],
      filteredObjects: [] as ObjectobjectObject[],
      usedContacts: [] as Record<string, string>[],
      selectedRoom: null as HandoverRoom | null,
      editingRoom: null as HandoverRoom | null
    }
  },
  computed: {
    accountId(): string | null {
      return this.$store.getters['user/getAccountId'] || null
    },
    objectId(): string {
      return this.object?.id || 'object'
    },
    employeeContacts() {
      let empContacts = this.contacts.filter(c => c.types.includes('EMPLOYEE'))
      if (this.getEmployees().length > 0) {
        empContacts = empContacts.filter(contact => {
          const matchingEmployee = this.getEmployees()
            .filter(employee => !employee.blocked)
            .find(employee => employee?.email?.toLowerCase()?.trim() === contact?.email?.toLowerCase()?.trim())

          return matchingEmployee !== undefined && matchingEmployee !== null
        })
      }
      return empContacts
    }
  },
  watch: {
    selectedRooms(newVal: HandoverRoom[]) {
      this.$emit('selected-rooms', newVal, this.object ? this.object : null, 'rooms')
    },
    deletedRooms(newVal: (string | number)[]) {
      this.$emit('deleted-rooms', newVal, this.object ? this.object : null)
    },
    selfRooms(newVal: HandoverRoom[], oldVal: HandoverRoom[]) {
      if (JSON.stringify(newVal) !== JSON.stringify(oldVal) || JSON.stringify(newVal) !== JSON.stringify(this.rooms)) {
        this.rooms = newVal
        this.updateSelectedRoom()
      }
    },
    suggestionRooms(newVal: HandoverRoom[], oldVal: HandoverRoom[]) {
      if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
        this.initAvailableRooms()
      }
    },
    '$i18n.locale'() {
      if (this.suggestionRooms.length === 0) {
        this.initAvailableRooms()
      }
    }
  },
  mounted() {
    this.initAvailableRooms()
    this.$emit('selected-rooms', this.selectedRooms, this.object || null, 'rooms')
    this.updateSelectedRoom()
  },
  methods: {
    ...mapActions('employee', ['loadEmployees']),
    ...mapGetters('employee', ['getEmployees']),
    initAvailableRooms() {
    if (this.suggestionRooms.length > 0) {
      this.availableRooms = this.suggestionRooms
        .map(room => ({
          ...room,
          id: room.id || `room_${Date.now()}`
        }))
    } else {
      this.availableRooms = []
      if (this.suggestionRooms.length === 0 || this.object === null || this.object.category === 'APARTMENT' || this.object.category === 'HOUSE') {
        this.availableRooms.push(
          {
            id: 'kitchen',
            name: this.$t('message.processHandover.rooms.kitchen'),
            dimensions: { area: null, height: null, length: null, volume: null, width: null },
            documentIds: [],
            imageMediaIds: [],
            note: '',
            ownerId: this.object?.ownerId || this.accountId,
            referenceId: this.object?.id || 'template'
          },
          {
            id: 'wetRoom',
            name: this.$t('message.processHandover.rooms.wetRoom'),
            dimensions: { area: null, height: null, length: null, volume: null, width: null },
            documentIds: [],
            imageMediaIds: [],
            note: '',
            ownerId: this.object?.ownerId || this.accountId,
            referenceId: this.object?.id || 'template'
          },
          {
            id: 'room',
            name: this.$t('message.processHandover.rooms.room'),
            dimensions: { area: null, height: null, length: null, volume: null, width: null },
            documentIds: [],
            imageMediaIds: [],
            note: '',
            ownerId: this.object?.ownerId || this.accountId,
            referenceId: this.object?.id || 'template'
          },
          {
            id: 'corridor_entrance',
            name: this.$t('message.processHandover.rooms.corridor_entrance'),
            dimensions: { area: null, height: null, length: null, volume: null, width: null },
            documentIds: [],
            imageMediaIds: [],
            note: '',
            ownerId: this.object?.ownerId || this.accountId,
            referenceId: this.object?.id || 'template'
          },
          {
            id: 'reduit_storage',
            name: this.$t('message.processHandover.rooms.reduit_storage'),
            dimensions: { area: null, height: null, length: null, volume: null, width: null },
            documentIds: [],
            imageMediaIds: [],
            note: '',
            ownerId: this.object?.ownerId || this.accountId,
            referenceId: this.object?.id || 'template'
          },
          {
            id: 'balcony_seatingArea',
            name: this.$t('message.processHandover.rooms.balcony_seatingArea'),
            dimensions: { area: null, height: null, length: null, volume: null, width: null },
            documentIds: [],
            imageMediaIds: [],
            note: '',
            ownerId: this.object?.ownerId || this.accountId,
            referenceId: this.object?.id || 'template'
          },
          {
            id: 'cellar',
            name: this.$t('message.processHandover.rooms.cellar'),
            dimensions: { area: null, height: null, length: null, volume: null, width: null },
            documentIds: [],
            imageMediaIds: [],
            note: '',
            ownerId: this.object?.ownerId || this.accountId,
            referenceId: this.object?.id || 'template'
          }
        )
      }
      if (this.suggestionRooms.length === 0 || this.object === null || this.object.category === 'PARKING_SPACE') {
        this.availableRooms.push({
          id: 'parking',
          name: this.$t('message.processHandover.rooms.parking'),
          dimensions: { area: null, height: null, length: null, volume: null, width: null },
          documentIds: [],
          imageMediaIds: [],
          note: '',
          ownerId: this.object?.ownerId || this.accountId,
          referenceId: this.object?.id || 'template'
        })
      }
      if (this.suggestionRooms.length === 0 || this.object === null || this.object.category === 'SECONDARY_ROOM') {
        this.availableRooms.push({
          id: 'secondaryRoom',
          name: this.$t('message.processHandover.rooms.secondaryRoom'),
          dimensions: { area: null, height: null, length: null, volume: null, width: null },
          documentIds: [],
          imageMediaIds: [],
          note: '',
          ownerId: this.object?.ownerId || this.accountId,
          referenceId: this.object?.id || 'template'
        })
      }
    }
    },
    updateOrder() {
      this.selectedRooms.forEach((room, index) => {
        room.order = index + 1
        room.referenceId = this.object?.id || 'template'
        if (room?.id && typeof room.id === 'string' && room.id.startsWith('room_')) {
          room.edited = true
        }
      })
    },
    updateSelectedRoom() {
      let rooms: HandoverRoom[] = []
      this.preSelectedRooms.forEach(room => {
        rooms.push(room)
      })
      this.rooms.forEach(room => {
        rooms.push(room)
      })
      rooms = rooms.filter((room, index, self) =>
        index === self.findIndex((t) => t.id === room.id)
      )
      this.selectedRooms = this.sortRooms(rooms)
    },
    sortRooms(rooms: HandoverRoom[]): HandoverRoom[] {
      return rooms.sort((a, b) => {
        if (a.order === -1) return 1
        if (b.order === -1) return -1
        return a.order - b.order
      })
    },
    addRoomMethod(room: HandoverRoom) {
      this.selectedRoom = room
      this.$nextTick(() => {
        this.moveSelectedRoomToSelected(room)
      })
    },
    moveSelectedRoomToSelected(room: HandoverRoom) {
      room = JSON.parse(JSON.stringify(room))
      room.id = `temproom_${Date.now()}`
      room.order = this.selectedRooms.length + 1
      room.referenceId = this.object?.id || 'template'
      this.selectedRooms.push(room)
      this.clearSelection()
    },
    showDeleteConfirmation(room: HandoverRoom, e: Event) {
      e.preventDefault()
      this.selectedRoom = room
      this.$bvModal.show('confirmationModalRoom_' + this.objectId)
    },
    clearSelection() {
      this.selectedRoom = null
    },
    addRoom() {
      const newRoom: HandoverRoom = {
        id: String(Date.now()),
        dimensions: { area: null, height: null, length: null, volume: null, width: null },
        documentIds: [],
        imageMediaIds: [],
        name: this.$t('message.onBoarding.devices.room'),
        note: '',
        ownerId: this.object?.ownerId || this.accountId,
        referenceId: this.object?.id || 'template',
        devices: []
      }
      this.selectedRooms.push(newRoom)
    },
    editRoom(room: HandoverRoom) {
      this.editingRoom = { ...room }
    },
    finishEdit(room: HandoverRoom) {
      if (room) {
        if (typeof room.id === 'string' && room.id.startsWith('room_')) {
          room.edited = true
        }
        const roomIndex = this.selectedRooms.findIndex((r) => r.id === room.id)
        if (roomIndex !== -1) {
          this.$set(this.selectedRooms, roomIndex, room)
        }
      }
      this.editingRoom = null
    },
    deleteRoom() {
      this.selectedRooms = this.selectedRooms.filter(r => this.selectedRoom.id !== r.id)
      this.deletedRooms.push(this.selectedRoom.id)
      this.$bvModal.hide('confirmationModalRoom_' + this.objectId)
      this.clearSelection()
    }
  }
})
</script>

<style lang="scss" scoped>
.save-icon {
  position: absolute;
  right: 20px;
  top: 12px;
  z-index: 9;
}
.container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card {
  width: 40%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  background-color: #f9f9f9;
}

.arrows {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.arrows button {
  margin: 5px;
}

.list-group {
  min-height: 200px;
  border: 1px solid #ddd;
  border-radius: 3px;
  padding: 10px;
  background-color: #fff;
}

.list-group-item {
  padding: 10px;
  margin: 5px 0;
  border: 1px solid #ddd;
  border-radius: 3px;
  background-color: #f5f5f5;
  min-height: 46px;
}

.list-group-item.selected {
  background-color: #d3e2ff;
  border-color: #007bff;
}
.tooltip-message {
  width: 15px;
}
</style>
